import React, { useState, useEffect } from 'react';
import { X, Loader2, Flame, Beef, Wheat, Droplets, Clock, ChefHat, Lightbulb } from 'lucide-react';
import { FoodThumbnail } from '@/components/FoodThumbnail';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API = '/api';

export function RecipeDetailModal({ isOpen, onClose, name, cal, protein, carbs, fat, items, cook_time }) {
  const { authHeaders } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen && !recipe) {
      fetchRecipe();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchRecipe = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.post(`${API}/ai-coach/generate-recipe`, {
        name, items: items || '', cal: parseInt(cal) || 0,
        protein: parseInt(protein) || 0, carbs: parseInt(carbs) || 0,
        fat: parseInt(fat) || 0, cook_time: cook_time || '',
      }, authHeaders());
      setRecipe(res.data);
    } catch {
      setError(true);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="fixed z-[81] inset-x-4 top-[10%] bottom-[10%] mx-auto max-w-lg overflow-y-auto rounded-2xl shadow-2xl"
        style={{ background: 'var(--bg-primary, #FAF8F5)' }}
        data-testid="recipe-detail-modal"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-black/5 dark:border-white/10 backdrop-blur-xl bg-white/90 dark:bg-[#141C22]/90 rounded-t-2xl">
          <h2 className="text-base font-bold text-[#1A3A4A] dark:text-[#F0EDE8] line-clamp-1">{name}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors" data-testid="recipe-modal-close">
            <X className="w-5 h-5 text-[#7A7A72]" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Hero */}
          <div className="flex items-center gap-4">
            <FoodThumbnail foodName={name} size="lg" />
            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {cal && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-[#C67A5C] bg-[#C67A5C]/10 px-2.5 py-1 rounded-full"><Flame className="w-3 h-3" />{cal} cal</span>}
                {protein && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-[#4A90D9] bg-[#4A90D9]/10 px-2.5 py-1 rounded-full"><Beef className="w-3 h-3" />{protein}g P</span>}
                {carbs && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-[#E5A84B] bg-[#E5A84B]/10 px-2.5 py-1 rounded-full"><Wheat className="w-3 h-3" />{carbs}g C</span>}
                {fat && <span className="flex items-center gap-0.5 text-[10px] font-semibold text-[#D4736A] bg-[#D4736A]/10 px-2.5 py-1 rounded-full"><Droplets className="w-3 h-3" />{fat}g F</span>}
              </div>
              {cook_time && (
                <span className="flex items-center gap-1 text-xs text-[#7A7A72] dark:text-[#A1A1AA]">
                  <Clock className="w-3.5 h-3.5" /> {cook_time} min
                </span>
              )}
            </div>
          </div>

          {loading && (
            <div className="flex flex-col items-center gap-3 py-10">
              <Loader2 className="w-7 h-7 animate-spin text-[#6B9E8A]" />
              <p className="text-sm text-[#7A7A72]">Generating recipe...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-[#D4736A]">Couldn't generate the recipe.</p>
              <button onClick={fetchRecipe} className="text-xs font-medium text-[#6B9E8A] underline">Try again</button>
            </div>
          )}

          {recipe && !loading && (
            <>
              {/* Ingredients */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-[#6B9E8A]/10 flex items-center justify-center">
                    <ChefHat className="w-4 h-4 text-[#6B9E8A]" />
                  </div>
                  <h3 className="text-sm font-bold text-[#1A3A4A] dark:text-[#F0EDE8]">Ingredients</h3>
                </div>
                <div className="space-y-1.5">
                  {recipe.ingredients.map((ing, i) => (
                    <div key={i} className="flex items-center gap-2.5 py-1.5 px-3 rounded-lg bg-[#1A3A4A]/[0.03] dark:bg-white/5">
                      <FoodThumbnail foodName={ing.name} size="sm" circular />
                      <span className="text-xs text-[#1A3A4A] dark:text-[#F0EDE8] flex-1">{ing.name}</span>
                      {ing.amount && <span className="text-[10px] text-[#7A7A72] dark:text-[#A1A1AA] font-medium">{ing.amount}</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Steps */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-[#C67A5C]/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-[#C67A5C]">1-{recipe.steps.length}</span>
                  </div>
                  <h3 className="text-sm font-bold text-[#1A3A4A] dark:text-[#F0EDE8]">Instructions</h3>
                </div>
                <div className="space-y-3">
                  {recipe.steps.map((step, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#C67A5C]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[10px] font-bold text-[#C67A5C]">{i + 1}</span>
                      </div>
                      <p className="text-xs text-[#4A5568] dark:text-[#A1A1AA] leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tips */}
              {recipe.tips && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-[#E5A84B]/8 border border-[#E5A84B]/15">
                  <Lightbulb className="w-4 h-4 text-[#E5A84B] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[#4A5568] dark:text-[#A1A1AA] leading-relaxed">{recipe.tips}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
